import { convertDateRangeToDates } from "@wingspanhq/fe-component-library";
import {
  ListFormsSortParameterValue,
  PayableStatus,
  PayeeReviewStatus,
  PayerApprovalStatus,
  SearchInvoiceRowsSortParameter,
  SearchPayableRowsFilterParameter
} from "@wingspanhq/wingspan-api-sdk";
import { UrlQuerySort } from "../../../../../shared/hooks/useUrlQuery";
import { getOverdueBoundary } from "../../../../../shared/utils/getOverdueBoundary";
import { getApiDirectionFromTable } from "../../../../../shared/utils/sorting";
import { isActiveStatusFilter, PAYABLES_STATUS_CLAUSES } from "./statusOptions";
import { Filter, PayablesTabFilter, SortField } from "./types";

/**
 * The v3 `searchPayableRows` request the payables table builds — a filter plus
 * an optional sort. Pagination is managed by the registry paginated-query hook,
 * so `page` is intentionally omitted here.
 */
export type PayableRowsSearchRequest = {
  filter: SearchPayableRowsFilterParameter;
  sort?: SearchInvoiceRowsSortParameter;
};

const baseFilter: SearchPayableRowsFilterParameter = {
  metadata_invoiceType: { ne: "approvedInvoicesPayment" },
  metadata_creationSource: { ne: "personalPaylink" }
};

// v1 payable statuses `Pending`/`Open`/`Overdue` collapse onto the v3
// `PayableStatus` values that back the pre-payment tabs (`Pending`, `Opened`);
// v3 has no separate `Overdue` (it is derived from `Opened` + a past due date).
const PRE_PAYMENT_STATUSES: PayableStatus[] = [
  PayableStatus.PENDING,
  PayableStatus.OPENED
];

/**
 * Build the tab's server-side `searchPayableRows` filter + sort. Ported from the
 * v1 `filter[status][in]` + `filter[payer.workflowStatus]` tab filters, mapped
 * onto the v3 `status` (raw lifecycle) + `payerApprovalStatus` fields so the
 * tabs still filter server-side and compose with pagination.
 *
 * Accepted approximations vs. v1 (recorded in V3_MIGRATION_TRACKER.md):
 * - The `PaymentInitiated`/`Funded` client-workflow states have no v3
 *   `payerApprovalStatus`, so the "scheduled" bucket is `Approved`-only.
 * - `internal.events.paymentDueAt` (payroll payment-due sort) has no v3 twin;
 *   the pre-payment tabs sort by `dueDate` instead.
 */
export function getTabFilter(
  tab: PayablesTabFilter,
  isDualStage = false
): PayableRowsSearchRequest {
  switch (tab) {
    case "draft":
      return {
        filter: { ...baseFilter, status: { anyOf: [PayableStatus.CREATED] } },
        sort: { updatedAt: ListFormsSortParameterValue.DESC }
      };
    case "to-approve":
      return {
        filter: {
          ...baseFilter,
          status: { anyOf: PRE_PAYMENT_STATUSES },
          payerApprovalStatus: {
            anyOf: isDualStage
              ? [PayerApprovalStatus.PRE_APPROVED]
              : [PayerApprovalStatus.PENDING, PayerApprovalStatus.PRE_APPROVED]
          }
        },
        sort: { dueDate: ListFormsSortParameterValue.ASC }
      };
    case "to-preapprove":
      return {
        filter: {
          ...baseFilter,
          status: { anyOf: PRE_PAYMENT_STATUSES },
          payerApprovalStatus: { anyOf: [PayerApprovalStatus.PENDING] }
        },
        sort: { dueDate: ListFormsSortParameterValue.ASC }
      };
    case "scheduled":
      return {
        filter: {
          ...baseFilter,
          status: { anyOf: PRE_PAYMENT_STATUSES },
          payerApprovalStatus: { anyOf: [PayerApprovalStatus.APPROVED] }
        },
        sort: { dueDate: ListFormsSortParameterValue.ASC }
      };
    case "paid":
      return {
        filter: {
          ...baseFilter,
          status: {
            anyOf: [PayableStatus.PAID, PayableStatus.PAYMENT_IN_TRANSIT]
          }
        },
        sort: { paidAt: ListFormsSortParameterValue.DESC }
      };
    case "disputed":
      // v1 filtered `member.workflowStatus = Disputed` across its "to approve"
      // and "scheduled" buckets, which together span every payer approval
      // state — so this constrains the lifecycle and the payee review only.
      return {
        filter: {
          ...baseFilter,
          status: { anyOf: PRE_PAYMENT_STATUSES },
          payeeReviewStatus: { anyOf: [PayeeReviewStatus.DISPUTED] }
        },
        sort: { updatedAt: ListFormsSortParameterValue.DESC }
      };
    default:
      // "all" — everything except cancelled.
      return {
        filter: {
          ...baseFilter,
          status: { noneOf: [PayableStatus.CANCELLED] }
        },
        sort: { updatedAt: ListFormsSortParameterValue.DESC }
      };
  }
}

/**
 * How many payable ids can be named in one `filter[id][anyOf]` before the query
 * string gets long enough to risk a gateway's URL limit. Ids are 22 characters
 * plus separators, so this is roughly 8KB of query string.
 */
const MAX_ANY_OF_IDS = 300;

type RequestOptions = {
  isDualStage?: boolean;
  overdueBefore?: Date;
  // Scopes the list to a single payroll run — the v3 `PayrollRun` id, which the
  // rows carry in their own right (the v1 filter was `internal.payroll.payrollId`
  // and the interim v3 one guessed at the payables' parent invoice).
  payrollRunId?: string;
  // The run's payables, resolved from `listPayables` because the row filter
  // above is applied but matches nothing (WIN-16127). Takes precedence over
  // `payrollRunId` while that holds.
  payableIds?: string[];
};

const toSortValue = (
  direction: ReturnType<typeof getApiDirectionFromTable>
): ListFormsSortParameterValue | undefined => {
  if (direction === "asc") {
    return ListFormsSortParameterValue.ASC;
  }
  if (direction === "desc") {
    return ListFormsSortParameterValue.DESC;
  }
  return undefined;
};

export function createSearchRequest(
  filter: Filter | undefined,
  sort: UrlQuerySort<SortField> | undefined,
  tab: PayablesTabFilter,
  options?: RequestOptions
): PayableRowsSearchRequest {
  const tabRequest = getTabFilter(tab, options?.isDualStage);
  const request: PayableRowsSearchRequest = {
    filter: { ...tabRequest.filter },
    sort: tabRequest.sort
  };

  // `filter[payrollRunId]` is the filter this should use, and the one it will
  // use again once WIN-16127 lands. Until then it matches nothing, so a caller
  // that resolved the run's payable ids narrows by those instead — except when
  // there are too many to name in a URL, where the intended filter is still the
  // better answer than a truncated list.
  if (
    options?.payableIds &&
    options.payableIds.length > 0 &&
    options.payableIds.length <= MAX_ANY_OF_IDS
  ) {
    request.filter.id = { anyOf: options.payableIds };
  } else if (options?.payrollRunId) {
    request.filter.payrollRunId = { eq: options.payrollRunId };
  }

  // A selected status replaces the tab's own status constraint outright — the
  // two describe the same fields, so narrowing the tab means overriding it.
  if (isActiveStatusFilter(filter?.status)) {
    const clause = PAYABLES_STATUS_CLAUSES[filter.status];
    request.filter.status = clause.status;
    request.filter.payerApprovalStatus = clause.payerApprovalStatus;
  }

  if (filter?.payeeId) {
    request.filter.payeeId = { eq: filter.payeeId };
  }

  if (filter?.payeeReviewStatus?.length) {
    request.filter.payeeReviewStatus = { anyOf: filter.payeeReviewStatus };
  }

  if (filter?.bulkBatchId) {
    request.filter.batchIds = { eq: filter.bulkBatchId };
  }

  if (filter?.dueDateRange) {
    const range = convertDateRangeToDates(filter.dueDateRange);
    if (range?.start || range?.end) {
      request.filter.dueDate = { gte: range?.start, lte: range?.end };
    }
  }

  if (filter?.status === "overdue") {
    request.filter.dueDate = {
      ...request.filter.dueDate,
      lt: options?.overdueBefore ?? getOverdueBoundary()
    };
  }

  if (filter?.paidDateRange) {
    const range = convertDateRangeToDates(filter.paidDateRange);
    if (range?.start || range?.end) {
      request.filter.events_paidAt = { gte: range?.start, lte: range?.end };
    }
  }

  if (filter?.openedDateRange) {
    const range = convertDateRangeToDates(filter.openedDateRange);
    if (range?.start || range?.end) {
      request.filter.events_openedAt = { gte: range?.start, lte: range?.end };
    }
  }

  // A user-selected column sort overrides the tab's default sort.
  const direction = toSortValue(getApiDirectionFromTable(sort?.direction));
  if (sort && direction) {
    if (sort.field === "createdAt") {
      request.sort = { createdAt: direction };
    } else if (sort.field === "updatedAt") {
      request.sort = { updatedAt: direction };
    } else if (sort.field === "events.paidAt") {
      request.sort = { paidAt: direction };
    }
  }

  return request;
}
